'use client'

import { useEffect, useState } from 'react'

import { firstNameOf } from '@/lib/name'

// Explicit-timezone formatting (admin-set tz) — safe to render during SSR
// since it never depends on the runtime's own timezone.
function formatRange(startIso, endIso, timeZone) {
  if (!startIso) return null
  try {
    const start = new Date(startIso)
    const dateFmt = new Intl.DateTimeFormat('en-US', { timeZone, dateStyle: 'full', timeStyle: 'short' })
    const startStr = dateFmt.format(start)
    if (!endIso) return startStr
    const endFmt = new Intl.DateTimeFormat('en-US', { timeZone, timeStyle: 'short' })
    return `${startStr} – ${endFmt.format(new Date(endIso))}`
  } catch {
    return null
  }
}

// Browser-local formatting depends on the environment's timezone, which
// differs between the server render and the browser — computed only inside
// an effect (post-mount) so the first paint on both sides matches exactly
// and there's no hydration mismatch; the local time then swaps in a beat
// later via a normal state update.
function useBrowserLocalRange(startIso, endIso) {
  const [state, setState] = useState({ text: null, tz: null })
  useEffect(() => {
    if (!startIso) return
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const dateFmt = new Intl.DateTimeFormat('en-US', { dateStyle: 'full', timeStyle: 'short' })
      let text = dateFmt.format(new Date(startIso))
      if (endIso) {
        const endFmt = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' })
        text = `${text} – ${endFmt.format(new Date(endIso))}`
      }
      setState({ text, tz })
    } catch {
      setState({ text: null, tz: null })
    }
  }, [startIso, endIso])
  return state
}

function SlotTime({ start, end, timezone }) {
  const adminText = formatRange(start, end, timezone || 'America/Phoenix')
  const { text: localText, tz } = useBrowserLocalRange(start, end)
  if (!start) {
    return <p className="mt-3 text-sm text-neutral-400">Time to be confirmed.</p>
  }
  return (
    <div className="mt-3">
      <p className="font-display text-xl font-medium text-white">
        {localText || adminText || 'Time to be confirmed.'}
      </p>
      {localText && tz && <p className="mt-1 text-xs text-neutral-500">Your timezone — {tz}</p>}
      {adminText && (
        <p className="mt-1 text-xs text-neutral-600">
          ({adminText}
          {timezone ? ` ${timezone}` : ''} — our team&apos;s timezone)
        </p>
      )}
    </div>
  )
}

function formatDescription(format, location) {
  if (format === 'virtual') {
    return 'A video call — the join link arrives as soon as you confirm.'
  }
  if (format === 'in-person') {
    const address = location?.address
    return address ? (
      <>
        In person at <span className="text-neutral-200">{address}</span>.
        {location?.instructions ? (
          <span className="mt-1 block text-neutral-500">{location.instructions}</span>
        ) : null}
      </>
    ) : (
      'In person — address to follow.'
    )
  }
  return null
}

function Card({ children }) {
  return (
    <div className="mt-6 rounded-2xl border border-kingdom-gold/30 bg-kingdom-gold/[0.05] p-6 sm:p-8">
      {children}
    </div>
  )
}

function Chip({ children }) {
  return (
    <span className="inline-block rounded-full border border-kingdom-gold/40 px-2.5 py-0.5 font-mono text-[0.6rem] tracking-[0.2em] text-kingdom-gold uppercase">
      {children}
    </span>
  )
}

const MAX_NOTE_CHARS = 1000

async function postSchedule(token, payload) {
  const res = await fetch('/api/careers/schedule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...payload }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) {
    const message =
      data.error === 'closed'
        ? 'This scheduling link has already been settled — refresh the page to see its current status.'
        : data.error || 'Something went wrong — please try again.'
    throw new Error(message)
  }
  return data
}

function ConfirmedCard({ confirmed, firstName, postingTitle }) {
  const { format, confirmedAt, proposedEnd, timezone, roamJoinUrl, location, round2ConfirmedAt } =
    confirmed || {}
  const awaitingJoinLink = format === 'virtual' && !roamJoinUrl

  return (
    <Card>
      <Chip>Confirmed</Chip>
      <p className="mt-2.5 text-sm leading-relaxed text-white">
        You&apos;re all set{firstName ? `, ${firstName}` : ''}
        {postingTitle ? (
          <>
            {' '}
            for <span className="font-semibold text-neutral-200">{postingTitle}</span>
          </>
        ) : null}
        .
      </p>
      <SlotTime start={confirmedAt} end={proposedEnd} timezone={timezone} />

      {format === 'virtual' && roamJoinUrl && (
        <a
          href={roamJoinUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-block rounded-full border border-kingdom-gold/40 bg-kingdom-gold px-5 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65]"
        >
          Join the interview
        </a>
      )}

      {format === 'in-person' && location?.address && (
        <p className="mt-4 text-sm leading-relaxed text-neutral-300">
          <span className="text-neutral-500">Location:</span> {location.address}
          {location.instructions ? (
            <span className="mt-1 block text-neutral-500">{location.instructions}</span>
          ) : null}
        </p>
      )}

      {awaitingJoinLink ? (
        <p className="mt-5 text-xs text-neutral-500">
          Your join link is on its way — we&apos;ll email it as soon as it&apos;s ready.
        </p>
      ) : round2ConfirmedAt ? (
        <p className="mt-5 text-xs text-neutral-500">The details are in your inbox.</p>
      ) : null}
    </Card>
  )
}

function ChangedCard({ firstName }) {
  return (
    <Card>
      <Chip>Noted</Chip>
      <p className="mt-2.5 text-sm leading-relaxed text-white">
        Got it{firstName ? `, ${firstName}` : ''} — we&apos;ll follow up with a new time that works
        better.
      </p>
      <p className="mt-2 text-xs text-neutral-500">
        No need to do anything else here; watch your inbox.
      </p>
    </Card>
  )
}

export function SchedulePanel({
  token,
  mode = 'proposal',
  candidateName = null,
  postingTitle = null,
  proposal = null,
  confirmed = null,
}) {
  const firstName = firstNameOf(candidateName)
  const [view, setView] = useState(mode) // 'proposal' | 'confirmed' | 'changed'
  const [confirmedResult, setConfirmedResult] = useState(confirmed)
  const [showChangeForm, setShowChangeForm] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function handleConfirm() {
    setBusy(true)
    setError(null)
    try {
      await postSchedule(token, { action: 'confirm' })
      setConfirmedResult({
        format: proposal?.format || null,
        confirmedAt: proposal?.proposedStart || null,
        proposedEnd: proposal?.proposedEnd || null,
        timezone: proposal?.timezone || null,
        roamJoinUrl: null,
        location: proposal?.location || null,
        round2ConfirmedAt: null,
      })
      setView('confirmed')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function handleChangeSubmit(e) {
    e.preventDefault()
    const trimmed = note.trim()
    if (!trimmed) {
      setError('Let us know what would work better before sending.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await postSchedule(token, { action: 'change', note: trimmed.slice(0, MAX_NOTE_CHARS) })
      setView('changed')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (view === 'confirmed') {
    return (
      <ConfirmedCard confirmed={confirmedResult} firstName={firstName} postingTitle={postingTitle} />
    )
  }

  if (view === 'changed') {
    return <ChangedCard firstName={firstName} />
  }

  const description = formatDescription(proposal?.format, proposal?.location)

  return (
    <Card>
      <Chip>Round 2</Chip>
      <p className="mt-2.5 text-sm leading-relaxed text-white">
        We&apos;d like to move forward
        {postingTitle ? (
          <>
            {' '}
            for <span className="font-semibold text-neutral-200">{postingTitle}</span>
          </>
        ) : null}
        . Here&apos;s the time we have in mind:
      </p>
      <SlotTime start={proposal?.proposedStart} end={proposal?.proposedEnd} timezone={proposal?.timezone} />
      {description && <p className="mt-3 text-sm leading-relaxed text-neutral-300">{description}</p>}

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-400">
          {error}
        </p>
      )}

      {!showChangeForm ? (
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="rounded-full border border-kingdom-gold/40 bg-kingdom-gold px-5 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-50"
          >
            {busy ? 'Confirming…' : 'Confirm this time'}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null)
              setShowChangeForm(true)
            }}
            disabled={busy}
            className="rounded-full border border-white/15 bg-neutral-950 px-5 py-2.5 text-sm font-medium text-neutral-300 transition hover:border-white/40 disabled:opacity-50"
          >
            Request a different time
          </button>
        </div>
      ) : (
        <form onSubmit={handleChangeSubmit} className="mt-6">
          <label htmlFor="reschedule-note" className="text-xs text-neutral-500">
            What would work better?
          </label>
          <textarea
            id="reschedule-note"
            name="note"
            rows={3}
            maxLength={MAX_NOTE_CHARS}
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setError(null)
            }}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? 'reschedule-note-error' : undefined}
            placeholder="A day/time range, or anything else we should know…"
            className="mt-2 block w-full resize-none rounded-2xl border border-white/15 bg-neutral-950 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:border-kingdom-gold/60 focus:outline-none"
          />
          {error && (
            <p id="reschedule-note-error" role="alert" className="mt-2 text-xs text-red-400">
              {error}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={busy || !note.trim()}
              className="rounded-full border border-kingdom-gold/40 bg-kingdom-gold px-5 py-2.5 text-sm font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Send request'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowChangeForm(false)
                setError(null)
              }}
              disabled={busy}
              className="rounded-full px-4 py-2.5 text-sm text-neutral-500 transition hover:text-neutral-300"
            >
              Back
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}
